import { queryOptions } from '@tanstack/react-query';

import { GitHubUser } from './user';

import { octokit } from '@/lib/clients';

// The returned event type is a MASSIVE union of all possible event types and it doesn't
// support type narrowing using event type so I simply manually type the events we
// care about.
// https://docs.github.com/en/rest/using-the-rest-api/issue-event-types?apiVersion=2022-11-28#committed

export type IssueEventBase = {
  id: number;
  node_id: string;
  url: string;
  actor: GitHubUser;
  event: string;
  commit_id: string;
  commit_url: string;
  created_at: string;
};

export type CommittedEvent = IssueEventBase & {
  event: 'committed';
  sha: string;
  message: string;
  author: {
    date: string;
    name: string;
    email: string;
  };
  committer: {
    date: string;
    name: string;
    email: string;
  };
  tree: object;
  parents: object[];
  verification: {
    verified: boolean;
    reason: string;
    signature: string;
    payload: string;
    verified_at: string;
  };
};

export type ClosedEvent = IssueEventBase & {
  event: 'closed';
};

export type ConvertedToDraftEvent = IssueEventBase & {
  event: 'converted_to_draft';
};

export type MergedEvent = IssueEventBase & {
  event: 'merged';
};

export type ReopenedEvent = IssueEventBase & {
  event: 'reopened';
};

export type ReadyForReviewEvent = IssueEventBase & {
  event: 'ready_for_review';
};

export type IssueEvent = ClosedEvent | ConvertedToDraftEvent | MergedEvent | ReopenedEvent | ReadyForReviewEvent;

export const timelineEventsQuery = (owner: string, repo: string, pull: number) =>
  queryOptions({
    queryKey: [owner, repo, 'pull', pull, 'timeline'],
    queryFn: async () => {
      const events = await octokit.paginate(
        octokit.rest.issues.listEventsForTimeline,
        {
          owner,
          repo,
          issue_number: pull,
          per_page: 100,
        },
        (response) =>
          response.data.filter((event) =>
            ['ready_for_review', 'converted_to_draft', 'merged', 'reopened', 'closed'].includes(event.event || '')
          )
      );
      // Filter out 'closed' events that have a matching 'merged' event at the same time
      const filteredEvents = events.filter((event) => {
        if (event.event === 'closed') {
          return !events.some(
            (e) => e.event === 'merged' && (e as MergedEvent).created_at === (event as ClosedEvent).created_at
          );
        }
        return true;
      });
      return filteredEvents.reverse() as IssueEvent[];
    },
    staleTime: 1000 * 60 * 5,
  });
